import {
  TASKS_VIEW_MODE_DEFAULT,
  type TasksViewMode,
  tasksViewModePropertyValues,
} from "@/types/userPreferences.ts";

/**
 * What the view menu calls each mode.
 *
 * Separate from the stored values, which are named for the shape each one
 * draws: see {@link tasksViewModePropertyValues}. A label is copy and will
 * move again; a stored value must not, or every reader's preference quietly
 * repoints at a different mode.
 *
 * This page's own words rather than the "Card view" / "List view" pair the
 * settings panels share. The vocabularies genuinely differ: a panel's two
 * shapes are a grid and a list, where these are told apart by how many tasks
 * are answered at once.
 */
export const TASKS_VIEW_MODE_LABEL: Record<TasksViewMode, string> = {
  rows: "Cards",
  lines: "Compact",
};

/**
 * The modes the view menu offers, in menu order: loosest first, so the list
 * reads as one axis of density rather than as unrelated choices.
 *
 * Here beside the labels rather than in either menu, because which modes are
 * offered is a fact about the product rather than about either app — and two
 * menus built from two private lists are two menus that drift.
 */
export const TASKS_VIEW_MODES: readonly TasksViewMode[] = tasksViewModePropertyValues;

/**
 * The stored preference, as a mode this build knows.
 *
 * The server hands back whatever it holds, and the client merges defaults only
 * for a property that is absent — so a value written by an older build comes
 * back intact, typed as a mode and matching none of them. Today that is every
 * reader who had chosen a view before these were renamed.
 *
 * Read once, here, rather than left to each comparison, so the menu always
 * has a row to tick. A `swipe` stored from when Triage was a mode comes back
 * intact and matches none of these, and falls through to the default like any
 * other value this build does not know.
 */
export function asTasksViewMode(stored: string): TasksViewMode {
  return tasksViewModePropertyValues.find((mode) => mode === stored) ?? TASKS_VIEW_MODE_DEFAULT;
}
